import { accountingService } from '../../helpers/services/accounting.service'
import i18n from '@/i18n'
import { sharedHelper } from '@/utils'

/**
 * OAuth ledger connections (Xero, QuickBooks Online).
 *
 * One store for both providers: the API shapes are identical, so the page
 * passes `provider` and this module routes to the matching service call.
 * AutoCount / SQL Account never come through here — they talk through the
 * on-premise bridge and have nothing to connect.
 */
export const state = {
  connection: { configured: false, connections: [] },
  status: { list: true, save: true },
}

export const ACCOUNTING_MODULE = 'accounting'

export const GET_CONNECTION_STATUS = 'getConnectionStatus'
export const GET_CONNECT_URL = 'getConnectUrl'
export const ACTIVATE_CONNECTION = 'activateConnection'
export const DISCONNECT_CONNECTION = 'disconnectConnection'

const REQUESTING = 'requesting'
const STATUS_SUCCESS = 'statusSuccess'
const SUCCESS = 'commonSuccess'
const FAILURE = 'commonFailure'

const SERVICES = {
  xero: {
    status: accountingService.getXeroStatus,
    connect: accountingService.getXeroConnectUrl,
    activate: accountingService.activateXeroConnection,
    disconnect: accountingService.disconnectXero,
  },
  quickbooks: {
    status: accountingService.getQuickbooksStatus,
    connect: accountingService.getQuickbooksConnectUrl,
    activate: accountingService.activateQuickbooksConnection,
    disconnect: accountingService.disconnectQuickbooks,
  },
}

function serviceFor(provider) {
  return SERVICES[provider] || null
}

function mapConnection(c) {
  const org = c.organisation || null
  return {
    uuid: c.uuid,
    tenantId: c.tenant_id,
    tenantName: c.tenant_name || (org && org.name) || '',
    isActive: c.is_active === true || c.is_active === 'T',
    expiresAt: c.expires_at || null,
    scopes: c.scopes || [],
    reachable: c.reachable === true,
    organisation: org
      ? {
          name: org.name || '',
          country: org.country || '',
          currency: org.currency || '',
          isDemo: org.is_demo === true,
        }
      : null,
  }
}

function fail(commit, error) {
  sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(error), error?.response?.data?.code)
  commit(FAILURE)
}

function unknownProvider(commit) {
  sharedHelper.alertErrorMessageWithoutCode(i18n.t('accountingConnection.unknownProvider'))
  commit(FAILURE)
  return Promise.resolve(false)
}

export const actions = {
  [GET_CONNECTION_STATUS]({ commit }, { provider }) {
    const svc = serviceFor(provider)
    if (!svc) return unknownProvider(commit)
    commit(REQUESTING, 'list')
    return svc
      .status()
      .then((result) => {
        commit(STATUS_SUCCESS, result?.data || {})
        commit(SUCCESS)
        return result
      })
      .catch((error) => fail(commit, error))
  },
  /** Resolves to the consent URL (string) on success, falsy on failure. */
  [GET_CONNECT_URL]({ commit }, { provider }) {
    const svc = serviceFor(provider)
    if (!svc) return unknownProvider(commit)
    commit(REQUESTING, 'save')
    return svc
      .connect()
      .then((result) => {
        commit(SUCCESS)
        return result?.data?.url || null
      })
      .catch((error) => fail(commit, error))
  },
  [ACTIVATE_CONNECTION]({ commit }, { provider, uuid, name }) {
    const svc = serviceFor(provider)
    if (!svc) return unknownProvider(commit)
    commit(REQUESTING, 'save')
    return svc
      .activate(uuid)
      .then((result) => {
        sharedHelper.alertSuccessMessage(i18n.t('accountingConnection.activated', [name || uuid]))
        commit(SUCCESS)
        return result || true
      })
      .catch((error) => fail(commit, error))
  },
  [DISCONNECT_CONNECTION]({ commit }, { provider, uuid, name }) {
    const svc = serviceFor(provider)
    if (!svc) return unknownProvider(commit)
    commit(REQUESTING, 'save')
    return svc
      .disconnect(uuid)
      .then((result) => {
        sharedHelper.alertSuccessMessage(i18n.t('accountingConnection.disconnected', [name || uuid]))
        commit(SUCCESS)
        return result || true
      })
      .catch((error) => fail(commit, error))
  },
}

export const mutations = {
  [STATUS_SUCCESS](state, data) {
    state.connection = {
      configured: data.configured === true,
      connections: (data.connections || []).map(mapConnection),
    }
  },
  [FAILURE](state) {
    state.status = { list: true, save: true }
  },
  [REQUESTING](state, type) {
    state.status[type] = false
  },
  [SUCCESS](state) {
    state.status = { list: true, save: true }
  },
}
